/**
 * ContractEstimateSlice
 * 
 * Redux slice for managing contract estimate state.
 * 
 * This slice follows the modular pattern with:
 * - state.ts: State interface and initial state
 * - selectors.ts: Memoized selectors
 * - reducers/: Split reducer files by domain
 */

import { createSlice } from '@reduxjs/toolkit';
import { initialState, ContractEstimateSliceState } from './state';

// Import all reducers (renamed to avoid conflicts with exported actions)
import {
  initializeEstimate as initializeEstimateReducer,
  setCurrentEstimate as setCurrentEstimateReducer,
  updateEstimate as updateEstimateReducer,
  clearEstimate as clearEstimateReducer,
  saveEstimate as saveEstimateReducer,
  loadEstimate as loadEstimateReducer,
  deleteEstimate as deleteEstimateReducer,
  addSavedEstimate as addSavedEstimateReducer,
  removeSavedEstimate as removeSavedEstimateReducer,
  setSavedEstimates as setSavedEstimatesReducer,
  addProcedureCode as addProcedureCodeReducer,
  updateProcedureCode as updateProcedureCodeReducer,
  removeProcedureCode as removeProcedureCodeReducer,
  reorderProcedureCodes as reorderProcedureCodesReducer,
  clearProcedureCodes as clearProcedureCodesReducer,
  setAvailableCenters as setAvailableCentersReducer,
  setAvailablePayers as setAvailablePayersReducer,
  setIsLoading as setIsLoadingReducer,
  setIsCalculating as setIsCalculatingReducer,
  setCalculationResult as setCalculationResultReducer,
  clearCalculationResult as clearCalculationResultReducer,
  setIsDirty as setIsDirtyReducer,
  setValidationError as setValidationErrorReducer,
  setValidationErrors as setValidationErrorsReducer,
  clearValidationError as clearValidationErrorReducer,
  clearAllValidationErrors as clearAllValidationErrorsReducer,
} from './reducers';

/**
 * Contract estimate slice definition
 */
const contractEstimateSlice = createSlice({
  name: 'contractEstimate',
  initialState,
  reducers: {
    // Estimate management
    initializeEstimate: initializeEstimateReducer,
    setCurrentEstimate: setCurrentEstimateReducer,
    updateEstimateBasicInfo: updateEstimateReducer,
    clearCurrentEstimate: clearEstimateReducer,
    saveCurrentEstimate: saveEstimateReducer,
    loadEstimate: loadEstimateReducer,
    deleteEstimate: deleteEstimateReducer,
    addSavedEstimate: addSavedEstimateReducer,
    removeSavedEstimate: removeSavedEstimateReducer,
    setSavedEstimates: setSavedEstimatesReducer,

    // Procedure codes management
    addProcedureCode: addProcedureCodeReducer,
    updateProcedureCode: updateProcedureCodeReducer,
    removeProcedureCode: removeProcedureCodeReducer,
    reorderProcedureCodes: reorderProcedureCodesReducer,
    clearProcedureCodes: clearProcedureCodesReducer,

    // Options management
    setAvailableCenters: setAvailableCentersReducer,
    setAvailablePayers: setAvailablePayersReducer,

    // UI state management
    setIsLoading: setIsLoadingReducer,
    setIsCalculating: setIsCalculatingReducer,
    setCalculationResult: setCalculationResultReducer,
    clearCalculationResult: clearCalculationResultReducer,
    setIsDirty: setIsDirtyReducer,
    setValidationErrors: setValidationErrorsReducer,
    clearValidationError: clearValidationErrorReducer,
    clearAllValidationErrors: clearAllValidationErrorsReducer,
  },
});

// Export actions
export const contractEstimateActions = contractEstimateSlice.actions;

// Export individual actions with Action suffix for convenience
export const {
  initializeEstimate: initializeEstimateAction,
  setCurrentEstimate: setCurrentEstimateAction,
  updateEstimateBasicInfo: updateEstimateBasicInfoAction,
  clearCurrentEstimate: clearCurrentEstimateAction,
  saveCurrentEstimate: saveCurrentEstimateAction,
  loadEstimate: loadEstimateAction,
  deleteEstimate: deleteEstimateAction,
  addSavedEstimate: addSavedEstimateAction,
  removeSavedEstimate: removeSavedEstimateAction,
  setSavedEstimates: setSavedEstimatesAction,
  addProcedureCode: addProcedureCodeAction,
  updateProcedureCode: updateProcedureCodeAction,
  removeProcedureCode: removeProcedureCodeAction,
  reorderProcedureCodes: reorderProcedureCodesAction,
  clearProcedureCodes: clearProcedureCodesAction,
  setAvailableCenters: setAvailableCentersAction,
  setAvailablePayers: setAvailablePayersAction,
  setIsLoading: setIsLoadingAction,
  setIsCalculating: setIsCalculatingAction,
  setCalculationResult: setCalculationResultAction,
  clearCalculationResult: clearCalculationResultAction,
  setIsDirty: setIsDirtyAction,
  setValidationErrors: setValidationErrorsAction,
  clearValidationError: clearValidationErrorAction,
  clearAllValidationErrors: clearAllValidationErrorsAction,
} = contractEstimateSlice.actions;

// Also export with original names for backward compatibility
export const {
  initializeEstimate,
  setCurrentEstimate,
  updateEstimateBasicInfo,
  clearCurrentEstimate,
  saveCurrentEstimate,
  loadEstimate,
  deleteEstimate,
  addSavedEstimate,
  removeSavedEstimate,
  setSavedEstimates,
  addProcedureCode,
  updateProcedureCode,
  removeProcedureCode,
  reorderProcedureCodes,
  clearProcedureCodes,
  setAvailableCenters,
  setAvailablePayers,
  setIsLoading,
  setIsCalculating,
  setCalculationResult,
  clearCalculationResult,
  setIsDirty,
  setValidationErrors,
  clearValidationError,
  clearAllValidationErrors,
} = contractEstimateSlice.actions;

// Alias for updateEstimateBasicInfo for convenience
export const updateEstimate = contractEstimateSlice.actions.updateEstimateBasicInfo;

// Export reducer
export default contractEstimateSlice.reducer;

// Re-export state types
export type { ContractEstimateSliceState } from './state';

// Re-export selectors
export * from './selectors';
